
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { useShoppingList } from '@/hooks/useShoppingList';
import { useShoppingMode } from '@/contexts/ShoppingModeContext';

import { 
  GenerateShoppingListForm,
  ShoppingListDisplay,
  ErrorDisplay
} from '@/components/shopping-list';

const ShoppingList = () => {
  const { preferredUnits, setPreferredUnits } = useUnitPreferences();
  const [formCollapsed, setFormCollapsed] = React.useState(false);
  const { isShoppingMode, setShoppingMode } = useShoppingMode();
  
  const {
    sixDays,
    availableDates,
    selectedDates,
    adults,
    kids,
    isLoadingShoppingList,
    isLoadingDates,
    shoppingList,
    error,
    processingStage,
    progress,
    mealPlansMap,
    isSaving,
    customItems,
    inactiveItems,
    addCustomItem,
    removeCustomItem,
    reactivateItem,
    toggleCustomItemChecked,
    setAdults,
    setKids,
    generateShoppingList,
    handleDateToggle,
    handleItemCheckedChange
  } = useShoppingList({ preferredUnits });

  const handleGenerate = async () => {
    await generateShoppingList();
    setFormCollapsed(true);
  };

  const handleExitShoppingMode = () => {
    setShoppingMode(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEnterShoppingMode = () => {
    setShoppingMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error) {
    return (
      <PageLayout 
        title="Shopping List"
        description="Generate a shopping list based on your meal plans"
      >
        <ErrorDisplay error={error} />
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Shopping List"
      description="Generate a shopping list based on your meal plans"
    >
      <div className="space-y-6">
        <GenerateShoppingListForm
          dates={sixDays}
          availableDates={availableDates}
          selectedDates={selectedDates}
          adults={adults}
          kids={kids}
          preferredUnits={preferredUnits}
          isLoadingShoppingList={isLoadingShoppingList}
          isLoadingDates={isLoadingDates}
          isCollapsed={formCollapsed}
          mealPlansMap={mealPlansMap}
          onDateToggle={handleDateToggle}
          onAdultsChange={setAdults}
          onKidsChange={setKids}
          onUnitChange={setPreferredUnits}
          onGenerate={handleGenerate}
          onToggleCollapse={() => setFormCollapsed(!formCollapsed)}
        />

        {(isLoadingShoppingList || shoppingList) && (
          <ShoppingListDisplay
            shoppingList={shoppingList}
            preferredUnits={preferredUnits}
            adults={adults}
            kids={kids}
            isLoadingShoppingList={isLoadingShoppingList}
            processingStage={processingStage}
            progress={progress}
            customItems={customItems}
            inactiveItems={inactiveItems}
            shoppingMode={isShoppingMode}
            onItemCheckedChange={handleItemCheckedChange}
            onCustomItemCheckedChange={toggleCustomItemChecked}
            onAddCustomItem={addCustomItem}
            onRemoveCustomItem={removeCustomItem}
            onReactivateItem={reactivateItem}
            onShoppingModeToggle={isShoppingMode ? handleExitShoppingMode : handleEnterShoppingMode}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default ShoppingList;
